import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { fetchApproved, reactTheory, submitTheory } from "@/lib/theories";
import { voterId } from "@/lib/votes";

const EASE = [0.22, 1, 0.36, 1];

export default function CommunityTheories({ siteId }) {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [author, setAuthor] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let live = true;
    setList(null);
    setOpen(false);
    setSent(false);
    fetchApproved(siteId, voterId())
      .then((l) => live && setList(l))
      .catch(() => live && setList([]));
    return () => {
      live = false;
    };
  }, [siteId]);

  const react = async (t) => {
    try {
      const updated = await reactTheory(t.id, voterId());
      setList((prev) =>
        prev.map((x) => (x.id === t.id ? updated : x)).sort((a, b) => b.compelling - a.compelling)
      );
    } catch {
      toast.error("Couldn't register that right now");
    }
  };

  const valid = title.trim().length >= 4 && body.trim().length >= 20;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      await submitTheory({ site_id: siteId, title, body, author });
      setSent(true);
      setOpen(false);
      setTitle("");
      setBody("");
      toast.success("Theory submitted — it appears here once the keeper has reviewed it");
    } catch (err) {
      toast.error(err.message || "Couldn't submit right now");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="community-theories" className="mt-9">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] tracking-[0.35em] text-[#38BDF8]">COMMUNITY THEORIES</p>
        <span data-testid="community-count" className="font-mono text-[8px] tracking-[0.25em] text-white/35">
          {list === null ? "…" : `${list.length} PUBLISHED`}
        </span>
      </div>

      {list && list.length > 0 && (
        <ul className="mt-4 space-y-3">
          {list.map((t) => (
            <li
              key={t.id}
              data-testid={`community-theory-${t.id}`}
              className="border border-[#38BDF8]/20 bg-[#181B26] p-4"
            >
              <p className="font-serif text-lg leading-snug text-[#F8FAFC]">{t.title}</p>
              <p className="mt-2 text-[13px] font-light leading-relaxed text-white/60">{t.body}</p>
              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="font-mono text-[8px] tracking-[0.25em] text-white/35">
                  — {t.author.toUpperCase()} · {t.reviewed_at?.slice(0, 10)}
                </p>
                <button
                  data-testid={`theory-react-${t.id}`}
                  onClick={() => react(t)}
                  aria-pressed={t.mine}
                  className={`flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[8px] tracking-[0.22em] transition-all duration-300 ${
                    t.mine
                      ? "border-[#38BDF8] bg-[#38BDF8] text-[#0B0C10]"
                      : "border-white/20 text-white/55 hover:border-[#38BDF8] hover:text-[#38BDF8]"
                  }`}
                >
                  ◆ COMPELLING <span data-testid={`theory-react-count-${t.id}`}>{t.compelling}</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {list && list.length === 0 && !sent && (
        <p className="mt-3 text-[13px] font-light leading-relaxed text-white/45">
          No visitor theories published yet for this site. Have one?
        </p>
      )}
      {sent && (
        <p data-testid="community-sent" className="mt-3 font-mono text-[9px] tracking-[0.2em] text-[#D4AF37]">
          YOUR THEORY IS IN THE REVIEW QUEUE
        </p>
      )}

      <button
        data-testid="propose-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`mt-4 w-full border py-3 font-mono text-[10px] tracking-[0.28em] transition-all duration-300 ${
          open
            ? "border-[#38BDF8] bg-[#38BDF8]/15 text-[#38BDF8]"
            : "border-white/20 text-white/60 hover:border-[#38BDF8] hover:text-[#38BDF8]"
        }`}
      >
        {open ? "− CLOSE" : "+ PROPOSE A THEORY"}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.form
            data-testid="propose-form"
            onSubmit={submit}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="mt-3 space-y-3 border border-[#38BDF8]/25 bg-[#0B0C10]/60 p-4">
              <input
                data-testid="propose-title"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, 90))}
                placeholder="Title — one line, like a headline"
                className="w-full border-b border-white/15 bg-transparent pb-2 font-serif text-lg text-[#F8FAFC] placeholder:text-white/25 focus:border-[#38BDF8]/60 focus:outline-none"
              />
              <textarea
                data-testid="propose-body"
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, 900))}
                rows={4}
                placeholder="Your reading of the evidence (20–900 characters). Claims, not insults — the keeper reviews everything before it appears."
                className="w-full resize-y border border-white/15 bg-transparent p-3 text-[13px] font-light leading-relaxed text-[#F8FAFC] placeholder:text-white/25 focus:border-[#38BDF8]/60 focus:outline-none"
              />
              <div className="flex flex-wrap items-center gap-3">
                <input
                  data-testid="propose-author"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value.slice(0, 40))}
                  placeholder="Sign it (optional)"
                  className="min-w-0 flex-1 border-b border-white/15 bg-transparent pb-1.5 font-mono text-[10px] tracking-[0.15em] text-[#F8FAFC] placeholder:text-white/25 focus:border-[#38BDF8]/60 focus:outline-none"
                />
                <span className="font-mono text-[8px] tracking-[0.2em] text-white/30">{body.length}/900</span>
                <button
                  data-testid="propose-submit"
                  type="submit"
                  disabled={!valid || busy}
                  className="border border-[#38BDF8] bg-[#38BDF8]/15 px-4 py-2 font-mono text-[9px] tracking-[0.25em] text-[#38BDF8] transition-colors hover:bg-[#38BDF8] hover:text-[#0B0C10] disabled:cursor-not-allowed disabled:opacity-30"
                >
                  {busy ? "SENDING…" : "SUBMIT FOR REVIEW"}
                </button>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
